"use client";

/**
 * Inicio 2.1 (rediseñado): ya no duplica el catálogo — destaca el boot del
 * mes, el pack variado, Pepa y accesos rápidos por categoría.
 */

import Link from "next/link";
import { useRouter } from "next/navigation";

import { BottomNav, HeaderLogo } from "@/components/app/chrome";
import { FotoBoot } from "@/components/app/foto-boot";
import { ProductoCard } from "@/components/app/producto-card";
import { PrecioDist } from "@/components/app/precio-dist";
import { useApp } from "@/components/app/proveedor";
import type { ProductoApp } from "@/components/app/tipos";

export function InicioCliente({ productos }: { productos: ProductoApp[] }) {
  const router = useRouter();
  const { descuentoDist } = useApp();
  const nuevo = productos.find((p) => p.nuevoDelMes);
  const destacados = productos.filter((p) => !p.nuevoDelMes).slice(0, 2);
  const categorias = Array.from(new Set(productos.map((p) => p.categoria)));

  return (
    <>
      <HeaderLogo />
      <main className="anim-bz-in min-h-(--alto-app) px-[17px] pt-[119px] pb-[62px]">
        <div className="pt-[34px] pb-[30px]">
          {nuevo && (
            <>
              <h2 className="font-app text-xl leading-5 font-semibold text-black">
                NUEVO ESTE MES
              </h2>
              <button
                type="button"
                onClick={() => router.push(`/productos/${nuevo.slug}`)}
                className="relative mt-6 block h-[126px] w-full overflow-hidden rounded-[30px] text-left"
                style={{ background: "linear-gradient(110deg,#1E8A40,#082411)" }}
              >
                <div className="absolute top-[26px] left-6 w-[200px]">
                  <div className="font-app text-[22px] leading-6 font-black text-white">
                    {nuevo.nombre}
                  </div>
                  <div className="mt-1.5 text-[13px] leading-[18px] text-white/75">
                    {nuevo.descripcion}
                  </div>
                </div>
                <span
                  className="absolute top-3.5 right-3 h-[98px] w-[98px] overflow-hidden rounded-full"
                  style={{ background: nuevo.gradiente }}
                >
                  {nuevo.imagen && (
                    <FotoBoot
                      imagen={nuevo.imagen}
                      gradiente={nuevo.gradiente}
                      alt={nuevo.nombre}
                      sizes="98px"
                    />
                  )}
                </span>
                <span className="absolute bottom-2 left-6 text-[13px] font-black text-lima">
                  210 g · <PrecioDist precio={nuevo.precio} descuento={descuentoDist} claseTachado="text-[11px] !text-white/50" />
                </span>
              </button>
            </>
          )}

          {/* Pack variado: el producto estrella del catálogo, sin duplicarlo. */}
          <Link
            href="/packs"
            className="relative mt-5 block overflow-hidden rounded-[30px] bg-[linear-gradient(160deg,#1E8A40,#082411)] p-6"
          >
            <div className="w-[240px] font-app text-[22px] leading-[26px] font-black text-white">
              Arma tu pack variado
            </div>
            <div className="mt-2 w-[230px] text-[13px] leading-[19px] text-white/70">
              Combina tus sabores favoritos en packs de 7 o 14 unidades.
            </div>
            <span className="mt-4 inline-flex h-10 items-center rounded-[20px] bg-lima px-[18px] font-app text-sm font-semibold text-bosque">
              Combinar sabores
            </span>
            <span className="absolute top-5 -right-4 flex h-24 w-24 rotate-12 flex-wrap gap-1 opacity-80">
              {productos.slice(0, 4).map((p) => (
                <span
                  key={p.slug}
                  className="h-11 w-11 rounded-xl"
                  style={{ background: p.gradiente }}
                />
              ))}
            </span>
          </Link>

          <div className="mt-[30px] flex items-baseline justify-between">
            <h2 className="font-app text-xl leading-5 font-semibold text-black">
              FAVORITOS DE LA CASA
            </h2>
            <Link href="/productos" className="text-[13px] text-verde">
              Ver catálogo
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4">
            {destacados.map((p) => (
              <ProductoCard key={p.slug} producto={p} conFavorito={false} />
            ))}
          </div>

          {/* Accesos por beneficio → catálogo. */}
          <div className="sin-scrollbar mt-5 flex gap-2 overflow-auto pb-1">
            {categorias.map((c) => (
              <Link
                key={c}
                href="/productos"
                className="flex h-[38px] flex-none items-center rounded-[20px] bg-humo px-[18px] font-app text-sm font-medium text-tinta"
              >
                {c}
              </Link>
            ))}
          </div>

          <button
            type="button"
            onClick={() => router.push("/pepa")}
            className="relative mt-[30px] block w-full overflow-hidden rounded-[30px] bg-bosque p-6 text-left"
          >
            <div className="w-[230px] font-app text-[22px] leading-[26px] font-black text-white">
              ¿No sabes cuál pedir?
            </div>
            <div className="mt-2 w-[240px] text-[13px] leading-[19px] text-white/70">
              Cuéntale a Pepa cómo te sientes y arma tu plan con dosis y
              frecuencia.
            </div>
            <span className="mt-4 inline-flex h-10 items-center gap-2 rounded-[20px] bg-lima px-[18px] font-app text-sm font-semibold text-bosque">
              Hablar con Pepa
            </span>
            <span className="absolute top-9 -right-3 h-20 w-20 rounded-full bg-lima/20" />
          </button>

          <div className="mt-5 grid grid-cols-2 gap-2.5">
            <Link
              href="/como-funciona"
              className="rounded-[22px] border border-black/5 bg-white p-4"
            >
              <span className="block font-app text-[15px] font-semibold">
                Cómo funciona
              </span>
              <span className="mt-1 block text-xs text-tinta/55">
                Licúa y listo, en 30 segundos
              </span>
            </Link>
            <Link
              href="/cobertura"
              className="rounded-[22px] border border-black/5 bg-white p-4"
            >
              <span className="block font-app text-[15px] font-semibold">
                Cobertura
              </span>
              <span className="mt-1 block text-xs text-tinta/55">
                5 zonas con día fijo
              </span>
            </Link>
          </div>
        </div>
      </main>
      <BottomNav />
    </>
  );
}
